'use client';

import { Dispatch, SetStateAction, useRef, useEffect } from 'react';

import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';

import { Entry } from '@/types/cit-public-api';

import { DEFAULT_BACKGROUND_COLOR, IFRAME_BACKGROUND_COLOR } from '@/lib/constants';

import { getContrastColor, isValidHexColor } from '@/lib/colorUtils';
import { queryFunctions } from '@/lib/query-functions';
import { getName, PLACEHOLDER_ENTRY } from '@/lib/properties';
import { getPrimaryMedium } from '@/lib/media-utils';

import { useCitOriginClient } from '@/app/CitOriginClientProvider';

import MediaViewer from './MediaViewer';
import PropertiesView from './PropertiesView';

interface EntryDetailLayoutProps {
  entry: Entry | null;
  enableGallery?: boolean;
  accountId: string;
  setButtonVisibility?: Dispatch<SetStateAction<'visible' | 'hidden'>>;
  closeModalButton?: React.ReactNode;
}

export default function EntryDetailLayout({
  enableGallery = true,
  entry,
  accountId,
  setButtonVisibility,
  closeModalButton
}: EntryDetailLayoutProps) {
  const entryToDisplay = entry || PLACEHOLDER_ENTRY;

  const citOrigin = useCitOriginClient();

  // tracks full page scroll on mobile
  const scrollRef = useRef<HTMLDivElement>(null);

  // tracks properties column scroll on desktop
  const propertiesScrollRef = useRef<HTMLDivElement>(null);

  // will recieve prefetched account details query from parent page
  const { data: accountDetails } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  useEffect(() => {
    let lastScrollTop = 0;
    let buttonsShouldBeVisible = false;

    const handleScroll = () => {
      if (!scrollRef.current || !setButtonVisibility) {
        return;
      }

      const element = scrollRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      // Check if we're at the bottom (with a small tolerance for rounding errors)
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 1;

      const isScrollingUp = scrollTop < lastScrollTop;
      const isScrollingDown = scrollTop > lastScrollTop;

      // Show buttons when scrolling up or at the bottom
      if (isScrollingUp || isAtBottom) {
        buttonsShouldBeVisible = true;
      }

      // Hide buttons when scrolling down (unless at bottom)
      if (isScrollingDown && !isAtBottom) {
        buttonsShouldBeVisible = false;
      }

      setButtonVisibility(buttonsShouldBeVisible ? 'visible' : 'hidden');

      // Update lastScrollTop for next comparison
      lastScrollTop = scrollTop;
    };
    const propertiesScroll = propertiesScrollRef.current;
    const pageScroll = scrollRef.current;

    if (pageScroll && propertiesScroll && setButtonVisibility) {
      pageScroll.addEventListener('scroll', handleScroll);

      // Set initial opacity with longer delay to ensure layout is complete
      setTimeout(handleScroll, 400);

      // Also use ResizeObserver to handle content changes
      const resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(pageScroll);

      return () => {
        pageScroll.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();

        // TODO: possibly will need to apply IOS scroll fix to this as well
        // reset scroll position to top for full page on mobile and properties column on desktop on unmount
        pageScroll.scrollTo({ top: 0, behavior: 'instant' });
        propertiesScroll.scrollTo({ top: 0, behavior: 'instant' });
      };
    }
  }, [setButtonVisibility, scrollRef]);

  const thumbnails = entryToDisplay.media || [];

  let bgColor: string;

  if (citOrigin === 'iframe') {
    bgColor = `#${IFRAME_BACKGROUND_COLOR}`;
  } else if (accountDetails?.theme?.backgroundColor) {
    bgColor = isValidHexColor(accountDetails.theme.backgroundColor)
      ? `#${accountDetails.theme.backgroundColor}`
      : `#${DEFAULT_BACKGROUND_COLOR}`;
  } else if (
    entryToDisplay.media &&
    getPrimaryMedium(entryToDisplay.media).derivatives.dominant_color
  ) {
    bgColor = `#${getPrimaryMedium(entryToDisplay.media).derivatives.dominant_color}`;
  } else {
    bgColor = `#${DEFAULT_BACKGROUND_COLOR}`;
  }

  const fgColor = getContrastColor(bgColor);

  return (
    <div
      className={`flex flex-col h-full w-full p-3 gap-6 overflow-y-auto lg:overflow-y-hidden`}
      style={{
        backgroundColor: bgColor,
        color: fgColor
      }}
      ref={scrollRef}
    >
      {/* Title Section */}
      <div className='flex flex-col w-full gap-1'>
        {/* if closeModalButton is provided, show it in the top right corner since we are in modal view */}
        {closeModalButton ? (
          <div className='flex justify-between items-center'>
            <h1 className='text-3xl font-bold truncate'>
              {getName(entryToDisplay.properties).replace(/"/g, '')}
            </h1>
            {closeModalButton}
          </div>
        ) : (
          <h1 className='text-3xl font-bold truncate'>
            {getName(entryToDisplay.properties).replace(/"/g, '')}
          </h1>
        )}
        <div className='flex flex-row gap-2 items-center'>
          <h2>{entryToDisplay.classification}</h2>
          {/* disable link in iframe view */}
          {citOrigin !== 'iframe' ? (
            <>
              <span>-</span>
              <Link
                href={`/${accountDetails?.slug || accountId}`}
                className='min-w-0 flex-1'
                prefetch={false}
              >
                <h3 className='truncate underline'>{accountDetails?.name}</h3>
              </Link>
            </>
          ) : null}
        </div>
      </div>

      {/* Media & Properties Block */}
      <div className='flex flex-col lg:flex-row w-full lg:h-full p-3 gap-6 lg:overflow-y-hidden'>
        {/* Media Column */}
        {/* if there is no media, we don't need to show the media column */}
        {entryToDisplay.media ? (
          <div className='flex flex-col w-full lg:w-7/12 lg:h-full lg:overflow-y-hidden'>
            <MediaViewer
              thumbnails={thumbnails}
              enableGallery={enableGallery}
              accountId={accountId}
            />
          </div>
        ) : null}

        {/* Properties Column */}
        <div
          className={`flex flex-col w-full lg:w-5/12 lg:h-full lg:overflow-y-auto gap-4 pb-14 lg:pb-0 ${
            entryToDisplay.media ? '' : 'lg:w-full'
          }`}
          ref={propertiesScrollRef}
        >
          <PropertiesView properties={entryToDisplay.properties} />
        </div>
      </div>
    </div>
  );
}
